import { useQuery } from '@apollo/client';
import { Button, ButtonVariant, InfoCard } from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';
import { TranslationsContext, UserContext } from '../../common/contexts';
import { ApplicationContext } from '../../common/contexts/ApplicationContext';
import { getNestedValue } from '../../common/utils/parsingUtils';
import { ErrorKey, ErrorType } from '../../common/utils/tracking/tracking.interfaces';
import { trackError } from '../../common/utils/tracking/trackingUtils';
import { GET_SALE, ISaleQueryData, ISaleQueryVariables } from './SalePage.queries';
import {
  CenteredCardContent,
  CenteredCardContentButton,
  CenteredCardContentItem,
  RightColumnItem,
} from './SalePage.styles';

export interface ISalePageProps {
  /**
   * Boolean indicating preview mode
   */
  isPreview?: boolean;
  /**
   * Sale identifier
   */
  saleId?: string;
  /**
   * JS method that is executed when user will pres join-for-free
   */
  loginUserFunction?: () => void;
}

/**
 * Sale Page
 */
const SalePage: React.FunctionComponent<ISalePageProps> = props => {
  const { saleId, isPreview = false, loginUserFunction } = props;
  const { translations } = React.useContext(TranslationsContext);
  const { affiliateCode, territoryCode, affiliateConfig } = React.useContext(ApplicationContext);
  const { userId } = React.useContext(UserContext);
  const showLoginBox: boolean = !userId && !affiliateConfig.isOpenSite;

  /**
   * Error handling for no sale id given (must be optional at application level)
   */
  if (!saleId) {
    return null;
  }
  const { error, data } = useQuery<ISaleQueryData, ISaleQueryVariables>(GET_SALE, {
    variables: {
      affiliate: affiliateCode,
      saleId,
      territory: territoryCode,
    },
  });

  if (error) {
    // Failing silently. Error will be tracked, but not displayed to the user.
    trackError(ErrorKey.SALE_LOAD, ErrorType.LOADING_ERROR, error.message, error);

    return null;
  }
  /**
   * Renders page layout
   */
  const weLike: string = getNestedValue(['sale', 'editorial', 'weLike'], data);
  const secondOpinion: string = getNestedValue(['sale', 'editorial', 'secondOpinion'], data);

  return (
    <div data-testid="salePage">
      {weLike && (
        <RightColumnItem>
          <InfoCard
            title={i18n('sale.weLike.title', [], translations)}
            dangerouslySetInnerHTML={weLike}
          />
        </RightColumnItem>
      )}
      {secondOpinion && !isPreview && (
        <RightColumnItem>
          <InfoCard
            title={i18n('sale.secondOpinion.title', [], translations)}
            dangerouslySetInnerHTML={secondOpinion}
          />
        </RightColumnItem>
      )}
      {showLoginBox && (
        <RightColumnItem>
          <InfoCard title={i18n('joinNow.text', [], translations)}>
            <CenteredCardContent>
              <CenteredCardContentItem>
                {i18n('sale.toViewAvailability.text', [], translations)}
              </CenteredCardContentItem>
              <CenteredCardContentButton>
                <Button variant={ButtonVariant.primary} onClick={loginUserFunction}>
                  {i18n('common.joinNow.label', [], translations)}
                </Button>
              </CenteredCardContentButton>
            </CenteredCardContent>
          </InfoCard>
        </RightColumnItem>
      )}
    </div>
  );
};

export default SalePage;
